import React, { Fragment, useEffect, useState } from "react";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col, Row } from "reactstrap";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useLoader } from "../../_helper/LoaderContext";
import { viewStockDetails } from "./StockService";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { FaArrowLeft } from "react-icons/fa6";
import { useCurrency } from "../../_helper/CurrencyContext";
import { shouldShowPurchaseTaxUi } from "../PharmacyManageTax/taxConfigUtils";

const calculateItemSubtotal = (item) => {
  const base = item.unitPrice * item.totalMedicine;
  const discount = Number(item.discount) || 0;
  return base - discount >= 0 ? base - discount : 0;
};

const calculateReturnSubtotal = (item) => {
  const base = item.unitPrice * item.totalMedicineReturned;
  return base;
};

const ViewStock = () => {
  const { t } = useTranslation();
  const { getCurrencySymbol } = useCurrency();

  const { stkId } = useParams();
  const [stock, setStock] = useState(null);
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [returnedPurchases, setReturnedPurchases] = useState([]);
  const { setLoading } = useLoader();
  const { pharmacyPOS } = usePharmacy();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStock = async () => {
      const body = { clinicId: pharmacyPOS?.hospital_id, purchaseId: stkId };
      try {
        setLoading(true);
        const response = await viewStockDetails(body);
        if (response.data?.status === "success") {
          setPurchaseDetails(response.data.data.purchaseDetails);
          setReturnedPurchases(response.data.data.returnedPurchases || []);
        }
      } catch (error) {
        console.error("Error fetching stock details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStock();
  }, [stkId]);

  if (!purchaseDetails) {
    return <div className="py-5 text-center">{t("Loading...")}</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Detect tax type
  const isIGST = purchaseDetails.isIGST;
  const taxType = purchaseDetails.taxType || (isIGST ? "gst" : "gst"); // fallback
  const isGST = taxType === "gst";
  const showPurchaseTax = shouldShowPurchaseTaxUi(pharmacyPOS, {
    purchaseTax: purchaseDetails.tax,
    items: purchaseDetails.stocks,
  });

  // Group by tax percentage
  const taxGroups = purchaseDetails.stocks.reduce((acc, item) => {
    const rate = item.taxPercentage || 0;
    if (!acc[rate]) acc[rate] = { taxable: 0, cgst: 0, sgst: 0, igst: 0, vat: 0, totalTax: 0 };

    const taxable = item.quantity * item.rate - (item.discount || 0);
    acc[rate].taxable += taxable;
    acc[rate].cgst += item.CGST || 0;
    acc[rate].sgst += item.SGST || 0;
    acc[rate].igst += item.IGST || 0;
    acc[rate].vat += item.VAT || 0;
    acc[rate].totalTax += item.tax || 0;
    return acc;
  }, {});

  const safeToFixed = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return "0.00";
    return Number(value).toFixed(decimals);
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={t("View Purchase Details")} parent={t("Dashboard")} title={t("View Purchase")} />
      <Col sm="12">
        <Card className="shadow-sm">
          <CardBody className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-end tw-w-full tw-mb-2">
              <button className="btn btn-primary" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-2" /> {t("Back")}
              </button>
            </div>
            {/* Header Section */}
            <Row className="mb-4">
              <Col md="8">
                <h4 className="mb-1 fw-bold">
                  {t("Purchase No")}: {purchaseDetails.purchaseNumber || "-"}
                </h4>
                <div>
                  {t("Purchase Date")}: {formatDate(purchaseDetails.purchaseDate)}
                </div>
                <div>
                  {t("Supplier")}: {purchaseDetails.supplierName || "-"}
                </div>
              </Col>
              <Col md="4" className="text-end">
                <h5 className="fw-bold">
                  {t("Total Paid")}: {purchaseDetails.currencyType === "USD" && '$'}{purchaseDetails.totalPaid?.toFixed(2)}{purchaseDetails.currencyType === "ZiG" && 'ZiG'}
                </h5>
              </Col>
            </Row>
            {/* Items Table */}
            <div className="table-responsive mb-4">
              <Table bordered hover size="sm" className="table-sm">
                <thead className="table-light">
                  <tr className="fw-bold">
                    <th>#</th>
                    <th>{t("Code")}</th>
                    <th>{t("Batch")}</th>
                    <th>{t("Brand/Product")}</th>
                    <th>{t("Salt/Composition")}</th>
                    <th>{t("Manufacturer Name")}</th>
                    <th>{t("Mfg Date")}</th>
                    <th>{t("Exp Date")}</th>
                    <th>{t("Pkg")}</th>
                    <th>{t("Pkg Qty")}</th>
                    <th>{t("Pkg Cost")}</th>
                    <th>{t("Discount")}</th>
                    <th>{t("Gross Value")}</th>
                    {showPurchaseTax && (
                      <>
                        <th>{t("Tax %")}</th>
                        {!isIGST ? (
                          <>
                            <th>{t("CGST %")}</th><th>{t("CGST")}</th>
                            <th>{t("SGST %")}</th><th>{t("SGST")}</th>
                          </>
                        ) : (
                          <>
                            <th>{t("IGST %")}</th><th>{t("IGST")}</th>
                          </>
                        )}
                      </>
                    )}
                    <th>{t("Unit Cost")}</th>
                    <th>{t("Pkg MRP")}</th>
                    <th>{t("Unit MRP")}</th>
                    <th>{t("Grand Total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseDetails.stocks.map((item, i) => {
                    const taxable = item.quantity * item.rate - (item.discount || 0);
                    const subtotal = taxable + (item.tax || 0);
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{item.code}</td>
                        <td>{item.batchNumber}</td>
                        <td style={{
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${item._id}`}>{item.brandName}</Tooltip>}>
                            <span>{item.brandName}</span>
                          </OverlayTrigger>
                        </td>
                        <td style={{
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${item._id}`}>{item.medicineName}</Tooltip>}>
                            <span>{item.medicineName}</span>
                          </OverlayTrigger>
                        </td>
                        <td
                          style={{
                            maxWidth: "100px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${item._id}`}>
                                {item.manufacturerName}
                              </Tooltip>
                            }
                          >
                            <span>{item.manufacturerName}</span>
                          </OverlayTrigger>
                        </td>
                        <td className="text-nowrap">{formatDate(item.manufacturingDate)}</td>
                        <td className="text-nowrap">{formatDate(item.expiryDate)}</td>
                        <td className="text-nowrap">{item.package} ({item.packing})</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-end">{getCurrencySymbol()}{item.rate.toFixed(2)}</td>
                        <td className="text-end">{item.discount || 0}</td>
                        <td className="text-end">
                          {getCurrencySymbol()}{item.taxableAmount.toFixed(2)}
                        </td>
                        {showPurchaseTax && (
                          <>
                            <td className="text-center">{item.taxPercentage}%</td>
                            {
                              isGST ? (
                                isIGST ? (
                                  <>
                                    <td>{item.IGSTPercentage || item.taxPercentage}</td>
                                    <td className="text-success fw-bold">{getCurrencySymbol()}{item.IGST?.toFixed(2)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td>{item.CGSTPercentage || (item.taxPercentage / 2).toFixed(1)}</td>
                                    <td className="text-success fw-bold">{getCurrencySymbol()}{item.CGST?.toFixed(2)}</td>
                                    <td>{item.SGSTPercentage || (item.taxPercentage / 2).toFixed(1)}</td>
                                    <td className="text-success fw-bold">{getCurrencySymbol()}{item.SGST?.toFixed(2)}</td>
                                  </>
                                )
                              ) : (
                                <>
                                  <td>{item.taxPercentage}</td>
                                  <td className="text-success fw-bold">{getCurrencySymbol()}{item.VAT?.toFixed(2)}</td>
                                </>
                              )
                            }
                          </>
                        )}
                        <td className="text-end">{getCurrencySymbol()}{item.unitPrice.toFixed(2)}</td>
                        <td className="text-end">{getCurrencySymbol()}{item.MRP.toFixed(2)}</td>
                        <td className="text-end">{getCurrencySymbol()}{item.unitPricePerMedicine.toFixed(2)}</td>
                        <td className="text-end fw-bold text-success">
                          {getCurrencySymbol()}{subtotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <Card className="border-2 border-primary mb-4">
              <CardBody className="bg-light">
                <Row className="align-items-end">
                  {showPurchaseTax && (
                  <Col md="8">
                    <h5 className="text-primary fw-bold mb-3">
                      {t("Tax Summary")}
                    </h5>
                    <div className="table-responsive sm:mb-4">
                      <Table bordered className="table-sm">
                        <thead>
                          <tr>
                            <th>{isGST ? "GST %" : "VAT %"}</th>
                            <th>Taxable Value</th>
                            {isGST && !isIGST && (
                              <>
                                <th>CGST Amount</th>
                                <th>SGST Amount</th>
                              </>
                            )}
                            {isGST && isIGST && <th>IGST Amount</th>}
                            {!isGST && <th>VAT Amount</th>}
                            <th>Total Tax</th>
                            <th>Net Tax Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(taxGroups)
                            .sort(([a], [b]) => a - b)
                            .map(([rate, g]) => (
                              <tr key={rate}>
                                <td className="text-center fw-bold">{rate}%</td>
                                <td className="text-end">{getCurrencySymbol()}{g.taxable.toFixed(2)}</td>
                                {isGST && !isIGST && (
                                  <>
                                    <td className="text-success">{getCurrencySymbol()}{g.cgst.toFixed(2)}</td>
                                    <td className="text-success">{getCurrencySymbol()}{g.sgst.toFixed(2)}</td>
                                  </>
                                )}
                                {isGST && isIGST && (
                                  <td className="text-success">{getCurrencySymbol()}{g.igst.toFixed(2)}</td>
                                )}
                                {!isGST && (
                                  <td className="text-success">{getCurrencySymbol()}{g.vat.toFixed(2)}</td>
                                )}
                                <td className="text-success text-end">{getCurrencySymbol()}{g.totalTax.toFixed(2)}</td>
                                <td className="text-end fw-bold">{getCurrencySymbol()}{(g.taxable + g.totalTax).toFixed(2)}</td>
                              </tr>
                            ))}

                          <tr className="fw-bold">
                            <td colSpan="4" className="text-end">{t("Total Tax Amt")}:</td>
                            <td className="text-end">{getCurrencySymbol()}{purchaseDetails.tax.toFixed(2)}</td>
                            {isGST && !isIGST && (
                              <td>
                                {/*  */}
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                  )}
                  <Col md={showPurchaseTax ? "4" : "12"}>
                    <h6 className="text-dark mb-3 d-flex justify-content-between">
                      {t("Net Taxable Value")}:
                      <span className="fw-bold text-success text-end">{getCurrencySymbol()}{purchaseDetails.taxableAmount.toFixed(2)}</span>
                    </h6>
                    <Table bordered className="table-sm">
                      <tbody>
                        {showPurchaseTax && !isIGST && isGST && (
                          <>
                            <tr>
                              <td className="text-end">Add: CGST</td>
                              <td className="text-end text-success fw-bold w-30">
                                {getCurrencySymbol()}{purchaseDetails.CGST?.toFixed(2) || "0.00"}
                              </td>
                            </tr>
                            <tr>
                              <td className="text-end">Add: SGST</td>
                              <td className="text-end text-success fw-bold w-30">
                                {getCurrencySymbol()}{purchaseDetails.SGST?.toFixed(2) || "0.00"}
                              </td>
                            </tr>
                          </>
                        )}
                        {showPurchaseTax && isIGST && (
                          <tr>
                            <td className="text-end">Add: IGST</td>
                            <td className="text-end text-success fw-bold w-30">
                              {getCurrencySymbol()}{purchaseDetails.IGST?.toFixed(2) || "0.00"}
                            </td>
                          </tr>
                        )}
                        {showPurchaseTax && !isGST && (
                          <tr>
                            <td className="text-end">Add: VAT</td>
                            <td className="text-end text-success fw-bold w-30">
                              {getCurrencySymbol()}{purchaseDetails.VAT?.toFixed(2) || "0.00"}
                            </td>
                          </tr>
                        )}
                        <tr className="table-success fw-bold fs-5">
                          <td className="text-end">GRAND TOTAL</td>
                          <td className="text-end text-primary">
                            {getCurrencySymbol()}{safeToFixed(purchaseDetails.NetTotal || (purchaseDetails.taxableAmount + purchaseDetails.tax))}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end fw-bold">{t("Total Paid")}</td>
                          <td className="text-end text-success fw-bold">
                            {getCurrencySymbol()}{safeToFixed(purchaseDetails.totalPaid)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end fw-bold">{t("Due Balance")}</td>
                          <td className="text-end text-danger fw-bold">
                            {getCurrencySymbol()}{safeToFixed((purchaseDetails.NetTotal || (purchaseDetails.taxableAmount + purchaseDetails.tax)) - (purchaseDetails.totalPaid || 0))}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* <Row className="mt-5">
              <Col md="6" className="text-end">
                <p className="mb-0"><strong>{t("Total Paid")}:</strong> {getCurrencySymbol()}{purchaseDetails.totalPaid?.toFixed(2)}</p>
                <p className="text-muted">{t("Payment Status")}: <span className="text-success fw-bold">{purchaseDetails.isPaid ? "Paid" : "Pending"}</span></p>
              </Col>
            </Row> */}
            {/* Payment History */}
            {purchaseDetails.purchaseAmountHistory && purchaseDetails.purchaseAmountHistory.length > 0 && (
              <div className="mt-4">
                <h5 className="fw-bold">{t("Payment History")}</h5>
                <div className="table-responsive">
                  <Table bordered className="table-sm">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>{t("Date")}</th>
                        <th>{t("Amount Paid")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseDetails.purchaseAmountHistory.map((payment, idx) => (
                        <tr key={payment._id || idx}>
                          <td>{idx + 1}</td>
                          <td>{formatDate(payment.date)}</td>
                          <td className="text-end text-success fw-bold">
                            {getCurrencySymbol()}{safeToFixed(payment.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            {/* Notes */}
            {purchaseDetails.notes && (
              <Row className="mt-4">
                <Col>
                  <label className="fw-bold">{t("Notes")}:</label>
                  <div>{purchaseDetails.notes}</div>
                </Col>
              </Row>
            )}
            {/* Returned Purchases Section */}
            {returnedPurchases.length > 0 && (
              <div className="mt-5">
                <h5 className="fw-bold text-danger mb-4">{t("Return History")}</h5>

                {returnedPurchases.map((ret, idx) => {
                  const isReturnIGST = ret.isIGST;
                  const returnTaxType = ret.taxType || (isReturnIGST ? "gst" : "gst");
                  const isReturnGST = returnTaxType === "gst";
                  const showReturnTax = shouldShowPurchaseTaxUi(pharmacyPOS, {
                    purchaseTax: ret.tax,
                    items: ret.stocks,
                  });

                  return (
                    <Card className="mb-4 border border-danger" key={ret._id}>
                      <CardBody className="p-4">
                        {/* Return Header */}
                        <Row className="mb-4">
                          <Col md="8">
                            <h6 className="fw-bold text-danger">
                              {t("Return No")}: {ret.purchaseNumber || "-"}
                            </h6>
                            <div className="text-muted">
                              {t("Return Date")}: {formatDate(ret.purchaseDate)}
                            </div>
                            <div className="text-muted">
                              {t("Supplier")}: {ret.supplierName}
                            </div>
                          </Col>
                          <Col md="4" className="text-end">
                            <h6 className="text-danger fw-bold">
                              {t("Total Return Amount")}:{" "}
                              {getCurrencySymbol()}
                              {ret.NetTotal?.toFixed(2)}
                            </h6>
                          </Col>
                        </Row>

                        {/* Returned Items Table */}
                        <div className="table-responsive mb-4">
                          <Table bordered hover size="sm" className="table-sm">
                            <thead className="table-light">
                              <tr className="fw-bold">
                                <th>#</th>
                                <th>{t("Code")}</th>
                                <th>{t("Batch")}</th>
                                <th>{t("Product")}</th>
                                <th>{t("Brand")}</th>
                                <th>{t("Manufacturer")}</th>
                                <th>{t("Pkg Qty")}</th>
                                <th>{t("Return Qty")}</th>
                                <th>{t("Pkg Cost")}</th>
                                <th>{t("Gross Value")}</th>
                                {showReturnTax && (
                                  <>
                                    <th>{t("Tax %")}</th>
                                    {!isReturnIGST ? (
                                      <>
                                        <th>{t("CGST %")}</th>
                                        <th>{t("CGST")}</th>
                                        <th>{t("SGST %")}</th>
                                        <th>{t("SGST")}</th>
                                      </>
                                    ) : (
                                      <>
                                        <th>{t("IGST %")}</th>
                                        <th>{t("IGST")}</th>
                                      </>
                                    )}
                                    <th>{t("Total Tax")}</th>
                                  </>
                                )}
                                <th>{t("Unit MRP")}</th>
                                <th>{t("Grand Total")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ret.stocks.map((item, i) => {
                                return (
                                  <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td>{item.code}</td>
                                    <td>{item.batchNumber}</td>

                                    <td>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-return-${i}`}>{item.brandName}</Tooltip>}
                                      >
                                        <span style={{
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "inline-block"
                                        }}>
                                          {item.brandName}
                                        </span>
                                      </OverlayTrigger>
                                    </td>
                                    <td>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-return-${i}`}>{item.medicineName}</Tooltip>}
                                      >
                                        <span style={{
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "inline-block"
                                        }}>
                                          {item.medicineName}
                                        </span>
                                      </OverlayTrigger>
                                    </td>
                                    <td>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-mfg-${i}`}>{item.manufacturerName}</Tooltip>}
                                      >
                                        <span style={{
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "inline-block"
                                        }}>
                                          {item.manufacturerName}
                                        </span>
                                      </OverlayTrigger>
                                    </td>

                                    <td className="text-center">{item.packing || "-"}</td>

                                    <td className="text-center">
                                      {item.totalMedicineReturned || item.quantity || 0}
                                    </td>

                                    {/* Package Rate (API → item.rate) */}
                                    <td className="text-end">
                                      {getCurrencySymbol()}
                                      {(item.rate || 0).toFixed(2)}
                                    </td>

                                    {/* Gross (API → item.taxableAmount) */}
                                    <td className="text-end">
                                      {getCurrencySymbol()}
                                      {(item.taxableAmount || 0).toFixed(2)}
                                    </td>

                                    {showReturnTax && (
                                      <>
                                        <td className="text-center">{item.taxPercentage || 0}%</td>
                                        {!ret.isIGST ? (
                                          <>
                                            <td className="text-center">{item.CGSTPercentage || 0}</td>
                                            <td className="text-end">{getCurrencySymbol()}{(item.CGST || 0).toFixed(2)}</td>
                                            <td className="text-center">{item.SGSTPercentage || 0}</td>
                                            <td className="text-end">{getCurrencySymbol()}{(item.SGST || 0).toFixed(2)}</td>
                                          </>
                                        ) : (
                                          <>
                                            <td className="text-center">{item.IGSTPercentage || 0}</td>
                                            <td className="text-end">{getCurrencySymbol()}{(item.IGST || 0).toFixed(2)}</td>
                                          </>
                                        )}
                                        <td className="text-end fw-bold text-success">
                                          {getCurrencySymbol()}
                                          {(item.tax || 0).toFixed(2)}
                                        </td>
                                      </>
                                    )}
                                    {/* Unit Rate (API → item.unitPrice) */}
                                    <td className="text-end">
                                      {getCurrencySymbol()}
                                      {(item.unitPrice || 0).toFixed(2)}
                                    </td>
                                    {/* Amount (API → item.totalPrice) */}
                                    <td className="text-end fw-bold text-danger">
                                      {getCurrencySymbol()}
                                      {(item.totalPrice || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>

                        {showReturnTax && (
                        <Row className="align-items-end">
                          <Col md="8">
                            <div className="table-responsive">
                              <Table bordered className="table-sm">
                                <thead className="table-light">
                                  <tr>
                                    <th className="text-center">{t("Summary")}</th>
                                    <th className="text-end">{t("Taxable Value")}</th>
                                    {isReturnGST && !isReturnIGST && (
                                      <>
                                        <th className="text-end">{t("CGST")}</th>
                                        <th className="text-end">{t("SGST")}</th>
                                      </>
                                    )}
                                    {isReturnGST && isReturnIGST && (
                                      <th className="text-end">{t("IGST")}</th>
                                    )}
                                    {!isReturnGST && (
                                      <th className="text-end">{t("VAT")}</th>
                                    )}
                                    <th className="text-end">{t("Total Tax")}</th>
                                    <th className="text-end">{t("Net Tax Value")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className="text-center fw-bold">{t("Return Summary")}</td>

                                    {/* Taxable */}
                                    <td className="text-end">
                                      {getCurrencySymbol()}
                                      {(ret.taxableAmount || 0).toFixed(2)}
                                    </td>

                                    {/* CGST/SGST or IGST */}
                                    {!ret.isIGST ? (
                                      <>
                                        <td className="text-end">{getCurrencySymbol()}{(ret.CGST || 0).toFixed(2)}</td>
                                        <td className="text-end">{getCurrencySymbol()}{(ret.SGST || 0).toFixed(2)}</td>
                                      </>
                                    ) : (
                                      <td className="text-end">{getCurrencySymbol()}{(ret.IGST || 0).toFixed(2)}</td>
                                    )}

                                    {/* Total tax */}
                                    <td className="text-end fw-bold text-success">
                                      {getCurrencySymbol()}
                                      {(ret.tax || 0).toFixed(2)}
                                    </td>

                                    {/* NetTotal */}
                                    <td className="text-end fw-bold text-danger">
                                      {getCurrencySymbol()}
                                      {(ret.NetTotal || ret.otherNetTotal || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>
                          </Col>
                          <Col md="4">
                            <Table bordered className="table-sm">
                              <tbody>
                                {!isReturnIGST && isReturnGST && (
                                  <>
                                    <tr>
                                      <td className="text-end">{t("CGST")}:</td>
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol()}
                                        {(ret.CGST || 0).toFixed(2)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-end">{t("SGST")}:</td>
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol()}
                                        {(ret.SGST || 0).toFixed(2)}
                                      </td>
                                    </tr>
                                  </>
                                )}
                                {isReturnIGST && (
                                  <tr>
                                    <td className="text-end">{t("IGST")}:</td>
                                    <td className="text-end text-success fw-bold">
                                      {getCurrencySymbol()}
                                      {(ret.IGST || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                )}
                                {!isReturnGST && (
                                  <tr>
                                    <td className="text-end">{t("VAT")}:</td>
                                    <td className="text-end text-success fw-bold">
                                      {getCurrencySymbol()}
                                      {(ret.VAT || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                )}
                                <tr className="table-danger fw-bold fs-5">
                                  <td className="text-end">{t("RETURN TOTAL")}</td>
                                  <td className="text-end text-danger">
                                    {getCurrencySymbol()}
                                    {(ret.NetTotal || 0).toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                        </Row>
                        )}

                        {/* Return Notes */}
                        {ret.notes && (
                          <Row className="mt-3">
                            <Col>
                              <label className="fw-bold">{t("Return Notes")}:</label>
                              <div className="text-muted">{ret.notes}</div>
                            </Col>
                          </Row>
                        )}
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Fragment>
    // <Fragment>
    //   <Breadcrumbs mainTitle={t("View Purchase")} parent={t("Dashboard")} title={t("View Purchase")} />
    //   <Col sm="12">
    //     <Card className="shadow-sm">
    //       <CardBody className="p-4">
    //         {/* Header */}
    //         <div className="d-flex justify-content-end tw-w-full tw-mb-2">
    //           <button className="btn btn-primary" onClick={() => navigate(-1)}>
    //             <FaArrowLeft className="me-2" /> {t("Back")}
    //           </button>
    //         </div>
    //         {/* Header Section */}
    //         <Row className="mb-4">
    //           <Col md="8">
    //             <h4 className="mb-1 fw-bold">
    //               {t("Purchase No")}: {purchaseDetails.purchaseNumber || "-"}
    //             </h4>
    //             <div>
    //               {t("Purchase Date")}: {formatDate(purchaseDetails.purchaseDate)}
    //             </div>
    //             <div>
    //               {t("Supplier")}: {purchaseDetails.supplierName || "-"}
    //             </div>
    //           </Col>
    //           <Col md="4" className="text-end">
    //             <h5 className="fw-bold">
    //               {t("Total Paid")}: {getCurrencySymbol()}{safeToFixed(purchaseDetails.totalPaid)}
    //             </h5>
    //           </Col>
    //         </Row>
    //         {/* Items Table */}
    //         <div className="table-responsive mb-4">
    //           <Table bordered hover size="sm" className="table-sm">
    //             <thead className="table-light">
    //               <tr className="fw-bold">
    //                 <th>#</th>
    //                 <th>{t("Medicine")}</th>
    //                 <th>{t("Batch")}</th>
    //                 <th>{t("Quantity")}</th>
    //                 <th>{t("Unit Price")}</th>
    //                 <th>{t("Total Price")}</th>
    //                 <th>{t("Discount")}</th>
    //                 <th>{t("Taxable Amount")}</th>
    //                 <th>{t("Tax %")}</th>
    //                 {isIGST ? (
    //                   <>
    //                     <th>{t("IGST %")}</th>
    //                     <th>{t("IGST")}</th>
    //                   </>
    //                 ) : (
    //                   <>
    //                     <th>{t("CGST %")}</th>
    //                     <th>{t("CGST")}</th>
    //                     <th>{t("SGST %")}</th>
    //                     <th>{t("SGST")}</th>
    //                   </>
    //                 )}
    //                 <th>{t("Total Tax")}</th>
    //                 <th>{t("MRP")}</th>
    //                 <th>{t("HSN")}</th>
    //               </tr>
    //             </thead>
    //             <tbody>
    //               {purchaseDetails.stocks.map((item, i) => (
    //                 <tr key={item.stockId || i}>
    //                   <td>{i + 1}</td>
    //                   <td style={{
    //                     maxWidth: "200px",
    //                     overflow: "hidden",
    //                     textOverflow: "ellipsis",
    //                     whiteSpace: "nowrap",
    //                   }}>
    //                     <OverlayTrigger
    //                       placement="top"
    //                       overlay={<Tooltip id={`tooltip-${item.itemId}`}>{item.medicineName}</Tooltip>}
    //                     >
    //                       <span>{item.medicineName}</span>
    //                     </OverlayTrigger>
    //                   </td>
    //                   <td>{item.batchNumber || "-"}</td>
    //                   <td className="text-center">{item.quantity || 0}</td>
    //                   <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.unitPrice)}</td>
    //                   <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.totalPrice)}</td>
    //                   <td className="text-end">{safeToFixed(item.discount, 0)}</td>
    //                   <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.taxableAmount)}</td>
    //                   <td className="text-center">{item.taxPercentage || 0}%</td>
    //                   {isIGST ? (
    //                     <>
    //                       <td className="text-center">{item.IGSTPercentage || item.taxPercentage || 0}%</td>
    //                       <td className="text-end text-success fw-bold">
    //                         {getCurrencySymbol()}{safeToFixed(item.IGST)}
    //                       </td>
    //                     </>
    //                   ) : (
    //                     <>
    //                       <td className="text-center">{item.CGSTPercentage || (item.taxPercentage / 2) || 0}%</td>
    //                       <td className="text-end text-success fw-bold">
    //                         {getCurrencySymbol()}{safeToFixed(item.CGST)}
    //                       </td>
    //                       <td className="text-center">{item.SGSTPercentage || (item.taxPercentage / 2) || 0}%</td>
    //                       <td className="text-end text-success fw-bold">
    //                         {getCurrencySymbol()}{safeToFixed(item.SGST)}
    //                       </td>
    //                     </>
    //                   )}
    //                   <td className="text-end fw-bold text-success">
    //                     {getCurrencySymbol()}{safeToFixed(item.tax)}
    //                   </td>
    //                   <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.MRP)}</td>
    //                   <td>{item.HSN || "-"}</td>
    //                 </tr>
    //               ))}
    //             </tbody>
    //           </Table>
    //         </div>

    //         <Card className="border-2 border-primary mb-4">
    //           <CardBody className="bg-light">
    //             {/* GST / VAT Summary Table */}
    //             <Row className="align-items-end">
    //               <Col md="8">
    //                 <h5 className="text-primary fw-bold mb-3">
    //                   {t("Tax Summary")}
    //                 </h5>
    //                 <div className="table-responsive sm:mb-4">
    //                   <Table bordered className="table-sm">
    //                     <thead className="table-primary">
    //                       <tr>
    //                         <th>{isGST ? "GST %" : "VAT %"}</th>
    //                         <th>Taxable Amount</th>
    //                         {isGST && !isIGST && (
    //                           <>
    //                             <th>CGST Amount</th>
    //                             <th>SGST Amount</th>
    //                           </>
    //                         )}
    //                         {isGST && isIGST && <th>IGST Amount</th>}
    //                         {!isGST && <th>VAT Amount</th>}
    //                         <th>Total Tax</th>
    //                         <th>Net Amount</th>
    //                       </tr>
    //                     </thead>
    //                     <tbody>
    //                       {Object.entries(taxGroups)
    //                         .sort(([a], [b]) => a - b)
    //                         .map(([rate, g]) => (
    //                           <tr key={rate}>
    //                             <td className="text-center fw-bold">{rate}%</td>
    //                             <td className="text-end">{getCurrencySymbol()}{safeToFixed(g.taxable)}</td>
    //                             {isGST && !isIGST && (
    //                               <>
    //                                 <td className="text-success">{getCurrencySymbol()}{safeToFixed(g.cgst)}</td>
    //                                 <td className="text-success">{getCurrencySymbol()}{safeToFixed(g.sgst)}</td>
    //                               </>
    //                             )}
    //                             {isGST && isIGST && (
    //                               <td className="text-success">{getCurrencySymbol()}{safeToFixed(g.igst)}</td>
    //                             )}
    //                             {!isGST && (
    //                               <td className="text-success">{getCurrencySymbol()}{safeToFixed(g.vat)}</td>
    //                             )}
    //                             <td className="text-success text-end">{getCurrencySymbol()}{safeToFixed(g.totalTax)}</td>
    //                             <td className="text-end fw-bold">{getCurrencySymbol()}{safeToFixed(g.taxable + g.totalTax)}</td>
    //                           </tr>
    //                         ))}

    //                       <tr className="fw-bold">
    //                         <td colSpan="4" className="text-end">{t("Total Tax Amt")}:</td>
    //                         <td className="text-end">{getCurrencySymbol()}{safeToFixed(purchaseDetails.tax)}</td>
    //                         {isGST && !isIGST && (
    //                           <td>
    //                             {/* Additional columns if needed */}
    //                           </td>
    //                         )}
    //                       </tr>
    //                     </tbody>
    //                   </Table>
    //                 </div>
    //               </Col>
    //               {/* Final Addition & Grand Total */}
    //               <Col md="4">
    //                 <h6 className="text-dark mb-3 d-flex justify-content-between">
    //                   {t("Total Taxable Amount")}:
    //                   <span className="fw-bold text-success text-end">
    //                     {getCurrencySymbol()}{safeToFixed(purchaseDetails.taxableAmount)}
    //                   </span>
    //                 </h6>
    //                 <Table bordered className="table-sm">
    //                   <tbody>
    //                     {!isIGST && isGST && (
    //                       <>
    //                         <tr>
    //                           <td className="text-end">Add: CGST</td>
    //                           <td className="text-end text-success fw-bold w-30">
    //                             {getCurrencySymbol()}{safeToFixed(purchaseDetails.CGST)}
    //                           </td>
    //                         </tr>
    //                         <tr>
    //                           <td className="text-end">Add: SGST</td>
    //                           <td className="text-end text-success fw-bold w-30">
    //                             {getCurrencySymbol()}{safeToFixed(purchaseDetails.SGST)}
    //                           </td>
    //                         </tr>
    //                       </>
    //                     )}
    //                     {isIGST && (
    //                       <tr>
    //                         <td className="text-end">Add: IGST</td>
    //                         <td className="text-end text-success fw-bold w-30">
    //                           {getCurrencySymbol()}{safeToFixed(purchaseDetails.IGST)}
    //                         </td>
    //                       </tr>
    //                     )}
    //                     <tr className="table-success fw-bold fs-5">
    //                       <td className="text-end">GRAND TOTAL</td>
    //                       <td className="text-end text-primary">
    //                         {getCurrencySymbol()}{safeToFixed(purchaseDetails.NetTotal)}
    //                       </td>
    //                     </tr>
    //                     <tr className="fw-bold">
    //                       <td className="text-end">Total Paid</td>
    //                       <td className="text-end">
    //                         {getCurrencySymbol()}{safeToFixed(purchaseDetails.totalPaid)}
    //                       </td>
    //                     </tr>
    //                     <tr className="fw-bold">
    //                       <td className="text-end">Balance Due</td>
    //                       <td className="text-end text-danger">
    //                         {getCurrencySymbol()}{safeToFixed(purchaseDetails.balance)}
    //                       </td>
    //                     </tr>
    //                   </tbody>
    //                 </Table>
    //               </Col>
    //             </Row>
    //           </CardBody>
    //         </Card>

    //         {/* Payment History */}
    //         {purchaseDetails.purchaseAmountHistory && purchaseDetails.purchaseAmountHistory.length > 0 && (
    //           <div className="mt-4">
    //             <h5 className="fw-bold">{t("Payment History")}</h5>
    //             <div className="table-responsive">
    //               <Table bordered className="table-sm">
    //                 <thead className="table-light">
    //                   <tr>
    //                     <th>#</th>
    //                     <th>{t("Date")}</th>
    //                     <th className="text-end">{t("Amount Paid")}</th>
    //                   </tr>
    //                 </thead>
    //                 <tbody>
    //                   {purchaseDetails.purchaseAmountHistory.map((payment, idx) => (
    //                     <tr key={payment._id || idx}>
    //                       <td>{idx + 1}</td>
    //                       <td>{formatDate(payment.date)}</td>
    //                       <td className="text-end text-success fw-bold">
    //                         {getCurrencySymbol()}{safeToFixed(payment.amount)}
    //                       </td>
    //                     </tr>
    //                   ))}
    //                 </tbody>
    //               </Table>
    //             </div>
    //           </div>
    //         )}

    //         {/* Notes */}
    //         {purchaseDetails.notes && (
    //           <Row className="mt-4">
    //             <Col>
    //               <label className="fw-bold">{t("Notes")}:</label>
    //               <div className="border p-3 rounded">{purchaseDetails.notes}</div>
    //             </Col>
    //           </Row>
    //         )}

    //         {/* Returned Purchases Section */}
    //         {returnedPurchases.length > 0 && (
    //           <div className="mt-5">
    //             <h5 className="fw-bold text-danger mb-4">{t("Return History")}</h5>

    //             {returnedPurchases.map((ret, idx) => {
    //               const isReturnIGST = ret.isIGST;
    //               const returnTaxType = ret.taxType || (isReturnIGST ? "gst" : "gst");
    //               const isReturnGST = returnTaxType === "gst";

    //               return (
    //                 <Card className="mb-4 border border-danger" key={ret._id}>
    //                   <CardBody className="p-4">
    //                     {/* Return Header */}
    //                     <Row className="mb-4">
    //                       <Col md="8">
    //                         <h6 className="fw-bold text-danger">
    //                           {t("Return No")}: {ret.purchaseNumber || "-"}
    //                         </h6>
    //                         <div className="text-muted">
    //                           {t("Return Date")}: {formatDate(ret.purchaseDate)}
    //                         </div>
    //                         <div className="text-muted">
    //                           {t("Supplier")}: {ret.supplierName}
    //                         </div>
    //                       </Col>
    //                       <Col md="4" className="text-end">
    //                         <h6 className="text-danger fw-bold">
    //                           {t("Total Return Amount")}:{" "}
    //                           {getCurrencySymbol()}{safeToFixed(ret.NetTotal)}
    //                         </h6>
    //                       </Col>
    //                     </Row>

    //                     {/* Returned Items Table - Simplified */}
    //                     <div className="table-responsive mb-4">
    //                       <Table bordered hover size="sm" className="table-sm">
    //                         <thead className="table-light">
    //                           <tr className="fw-bold">
    //                             <th>#</th>
    //                             <th>{t("Medicine")}</th>
    //                             <th>{t("Batch")}</th>
    //                             <th>{t("Quantity")}</th>
    //                             <th>{t("Unit Price")}</th>
    //                             <th>{t("Total Price")}</th>
    //                             <th>{t("Tax")}</th>
    //                           </tr>
    //                         </thead>
    //                         <tbody>
    //                           {ret.stocks && ret.stocks.map((item, i) => (
    //                             <tr key={i}>
    //                               <td>{i + 1}</td>
    //                               <td>{item.medicineName || "-"}</td>
    //                               <td>{item.batchNumber || "-"}</td>
    //                               <td className="text-center">{item.quantity || 0}</td>
    //                               <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.unitPrice)}</td>
    //                               <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.totalPrice)}</td>
    //                               <td className="text-end">{getCurrencySymbol()}{safeToFixed(item.tax)}</td>
    //                             </tr>
    //                           ))}
    //                         </tbody>
    //                       </Table>
    //                     </div>

    //                     {/* Return Summary */}
    //                     <Row>
    //                       <Col md="8" className="text-end">
    //                         <h6 className="fw-bold">
    //                           {t("Return Reason")}: <span className="text-muted">{ret.notes || "N/A"}</span>
    //                         </h6>
    //                       </Col>
    //                       <Col md="4">
    //                         <Table bordered className="table-sm">
    //                           <tbody>
    //                             <tr className="fw-bold">
    //                               <td>{t("Taxable Amount")}:</td>
    //                               <td className="text-end">{getCurrencySymbol()}{safeToFixed(ret.taxableAmount)}</td>
    //                             </tr>
    //                             <tr className="fw-bold">
    //                               <td>{t("Total Tax")}:</td>
    //                               <td className="text-end text-success">{getCurrencySymbol()}{safeToFixed(ret.tax)}</td>
    //                             </tr>
    //                             <tr className="table-danger fw-bold fs-5">
    //                               <td className="text-end">{t("RETURN TOTAL")}</td>
    //                               <td className="text-end text-danger">
    //                                 {getCurrencySymbol()}{safeToFixed(ret.NetTotal)}
    //                               </td>
    //                             </tr>
    //                           </tbody>
    //                         </Table>
    //                       </Col>
    //                     </Row>
    //                   </CardBody>
    //                 </Card>
    //               );
    //             })}
    //           </div>
    //         )}
    //       </CardBody>
    //     </Card>
    //   </Col>
    // </Fragment>
  );

  // return (
  //   <Fragment>
  //     <Breadcrumbs
  //       mainTitle={t("View Stock")}
  //       parent={t("Dashboard")}
  //       title={t("View Stock")}
  //     />

  //     <Col sm="12">
  //       <Card>
  //         <CardBody>
  //           <div className="d-flex justify-content-end tw-w-full tw-mb-2">
  //             <button className="btn btn-primary" onClick={() => navigate(-1)}>
  //               <FaArrowLeft className="me-2" /> {t("Back")}
  //             </button>
  //           </div>
  //           <Row className="mb-4">
  //             <Col md="8">
  //               <h4 className="mb-1 fw-bold">
  //                 {t("Purchase No")}: {stock.purchaseNumber || "-"}
  //               </h4>
  //               <div>
  //                 {t("Purchase Date")}: {formatDate(stock.purchaseDate)}
  //               </div>
  //               <div>
  //                 {t("Supplier")}: {stock.supplierName || "-"}
  //               </div>
  //             </Col>
  //             <Col md="4" className="text-end">
  //               <h5 className="fw-bold">
  //                 {t("Total Paid")}: ${stock.totalPaid.toFixed(2)}
  //               </h5>
  //             </Col>
  //           </Row>
  //           <div className="mb-4 table-responsive">
  //             <Table bordered size="sm" className="align-middle">
  //               <thead className="table-light">
  //                 <tr>
  //                   <th>#</th>
  //                   <th>{t("Code")}</th>
  //                   <th>{t("Batch No")}</th>
  //                   <th>{t("Brand Name")}</th>
  //                   <th>{t("Manufacturer Name")}</th>
  //                   <th className="text-nowrap">{t("Mfg Date")}</th>
  //                   <th className="text-nowrap">{t("Expiry Date")}</th>
  //                   <th>{t("Package")}</th>
  //                   <th className="text-center">{t("Quantity")}</th>
  //                   <th className="text-center">{t("Unit Quantity")}</th>
  //                   <th className="text-center">{t("Unit Purchase Price")}</th>
  //                   <th className="text-center">{t("Unit Sale Price")}</th>
  //                   <th className="text-center">{t("Discount")}</th>
  //                   <th className="text-center">{t("Subtotal")}</th>
  //                 </tr>
  //               </thead>
  //               <tbody>
  //                 {stock.stocks.map((item, index) => (
  //                   <tr key={index}>
  //                     <td>{index + 1}</td>
  //                     <td>{item.code}</td>
  //                     <td>{item.batchNumber}</td>
  //                     <td
  //                       style={{
  //                         maxWidth: "100px",
  //                         overflow: "hidden",
  //                         textOverflow: "ellipsis",
  //                         whiteSpace: "nowrap",
  //                       }}
  //                     >
  //                       <OverlayTrigger
  //                         placement="top"
  //                         overlay={
  //                           <Tooltip id={`tooltip-${item._id}`}>
  //                             {item.brandName}
  //                           </Tooltip>
  //                         }
  //                       >
  //                         <span>{item.brandName}</span>
  //                       </OverlayTrigger>
  //                     </td>
  //                     <td
  //                       style={{
  //                         maxWidth: "100px",
  //                         overflow: "hidden",
  //                         textOverflow: "ellipsis",
  //                         whiteSpace: "nowrap",
  //                       }}
  //                     >
  //                       <OverlayTrigger
  //                         placement="top"
  //                         overlay={
  //                           <Tooltip id={`tooltip-${item._id}`}>
  //                             {item.manufacturerName}
  //                           </Tooltip>
  //                         }
  //                       >
  //                         <span>{item.manufacturerName}</span>
  //                       </OverlayTrigger>
  //                     </td>
  //                     <td className="text-nowrap">
  //                       {formatDate(item.manufacturingDate)}
  //                     </td>
  //                     <td className="text-nowrap">
  //                       {formatDate(item.expiryDate)}
  //                     </td>
  //                     <td className="text-nowrap">
  //                       {item.package} ({item.packing})
  //                     </td>
  //                     <td className="text-center">{item.quantity}</td>
  //                     <td className="text-center">{item.totalMedicine}</td>
  //                     <td className="text-center">${item.unitPrice}</td>
  //                     <td className="text-center">
  //                       ${item.unitPricePerMedicine}
  //                     </td>
  //                     <td className="text-center">{item.discount || 0}</td>
  //                     <td className="text-center fw-bold text-success">
  //                       ${calculateItemSubtotal(item).toFixed(2)}
  //                     </td>
  //                   </tr>
  //                 ))}
  //               </tbody>
  //             </Table>
  //           </div>

  //           <Row className="justify-content-end">
  //             <Col md="6">
  //               <Table bordered size="sm">
  //                 <tbody>
  //                   <tr>
  //                     <td className="fw-bold">{t("Subtotal")}</td>
  //                     <td className="text-end">${subtotal.toFixed(2)}</td>
  //                   </tr>
  //                   <tr className="fw-bold">
  //                     <td>{t("Net Total")}</td>
  //                     <td className="text-end">${stock.NetTotal.toFixed(2)}</td>
  //                   </tr>
  //                   <tr className="fw-bold table-primary">
  //                     <td>{t("Total Paid")}</td>
  //                     <td className="text-end">
  //                       ${stock.totalPaid.toFixed(2)}
  //                     </td>
  //                   </tr>
  //                 </tbody>
  //               </Table>
  //             </Col>
  //           </Row>

  //           {stock.notes && (
  //             <Row className="mt-3">
  //               <Col md="12">
  //                 <label className="fw-bold">{t("Notes")}:</label>
  //                 <div>{stock.notes}</div>
  //               </Col>
  //             </Row>
  //           )}
  //         </CardBody>
  //       </Card>
  //     </Col>
  //   </Fragment>
  // );
};

export default ViewStock;
